import { useEffect, useMemo } from 'react';
import { Outlet, useOutletContext } from 'react-router-dom';
import { SendMessage } from 'react-use-websocket';
import isEqual from 'lodash.isequal';
import {
  PermissionEnum,
  IconLoader,
  Table,
  BaseTransaction,
  usePrevious,
} from '@ui/src';
import { useAppSelector, useAppDispatch } from '@store';
import {
  useLazyGetTransactionsDictionariesQuery,
  useLazyGetTransactionsBaseCurrencyQuery,
  useLazyGetTransactionsCurrenciesQuery,
  useLazyGetTransactionsCountriesQuery,
  useLazyGetTransactionsRateSourceQuery,
  useLazyGetTransactionsDictionariesWithoutGLQuery,
  useLazyGetTransactionsRateSourceSettingsQuery,
} from '../../store/dictionaries/services';
import { useLazyGetExchangeWithoutCorrectionQuery } from '../exchange/store/services';
import {
  columns,
  columnsLabels,
  expandableColumns,
} from '../exchange/variables/list';
import formatExchangeList from '../exchange/helpers/format-list';
import { useLazyGetGeneralLedgerTransactionValuesQuery } from '../transactions/store/services';
import { setExchangeWithoutCorrectionExpandedTransactionId } from '../exchange/store/slices/list';

import cn from '../exchange/styles.module.css';

const ExchangeWithoutCorrections = () => {
  const [getDictionaries] = useLazyGetTransactionsDictionariesQuery();
  const [getDictionariesWithoutGL] =
    useLazyGetTransactionsDictionariesWithoutGLQuery();
  const [getTransactionBaseCurrency] =
    useLazyGetTransactionsBaseCurrencyQuery();
  const [getTransactionCurrencies] = useLazyGetTransactionsCurrenciesQuery();
  const [getTransactionCountries] = useLazyGetTransactionsCountriesQuery();
  const [getTransactionRateSource] = useLazyGetTransactionsRateSourceQuery();
  const [getTransactionRateSourceSettings] =
    useLazyGetTransactionsRateSourceSettingsQuery();

  const { sendMessage } = useOutletContext<{ sendMessage: SendMessage }>();

  const {
    activePermissions,
    countries,
    currencies,
    currenciesList,
    baseCurrency,
    rateSources,
    accounts,
    baseRateSettings,
    pspPartnerAccounts,
  } = useAppSelector((state) => state.transactionsDictionaries);
  const { values: transactionsTypes } = useAppSelector(
    (state) => state.settings.transactionsTypes,
  );
  const {
    exchangeWithoutCorrections,
    exchangeWithoutCorrectionExpandedTransactionId,
    collapseAllExchangeWithoutCorrectionRows,
  } = useAppSelector((state) => state.exchange.list);

  const prevTransactions = usePrevious(exchangeWithoutCorrections);

  const dispatch = useAppDispatch();

  const [
    getGeneralLedgerTransactionValues,
    { isLoading: glIsLoading, isFetching, isSuccess },
  ] = useLazyGetGeneralLedgerTransactionValuesQuery();

  const getGeneralLedgerAction = (id: string) => {
    dispatch(setExchangeWithoutCorrectionExpandedTransactionId(id));
    getGeneralLedgerTransactionValues(id);
  };

  const [getList, { isLoading, isFetching: exchangeListFetching }] =
    useLazyGetExchangeWithoutCorrectionQuery();

  const formattedData = useMemo(() => {
    const data = formatExchangeList(
      exchangeWithoutCorrections as BaseTransaction[],
      currenciesList,
    );

    return data;
  }, [exchangeWithoutCorrections, currenciesList]);

  useEffect(() => {
    if (
      activePermissions &&
      activePermissions.includes(PermissionEnum.TRANSACTIONS_EXCHANGE_LIST)
    ) {
      getList();

      if (!accounts) {
        getDictionaries();
      }

      if (!pspPartnerAccounts) {
        getDictionariesWithoutGL();
      }

      if (!currencies) {
        getTransactionCurrencies();
      }

      if (!countries) {
        getTransactionCountries();
      }

      if (!baseCurrency) {
        getTransactionBaseCurrency();
      }

      if (!rateSources) {
        getTransactionRateSource();
      }

      if (!baseRateSettings) {
        getTransactionRateSourceSettings();
      }
    }
  }, [
    activePermissions,
    baseCurrency,
    currencies,
    countries,
    pspPartnerAccounts,
    rateSources,
    accounts,
    baseRateSettings,
  ]);

  useEffect(() => {
    if (
      !isEqual(prevTransactions, exchangeWithoutCorrections) &&
      exchangeWithoutCorrectionExpandedTransactionId
    ) {
      getGeneralLedgerAction(exchangeWithoutCorrectionExpandedTransactionId);
    }
  }, [
    prevTransactions,
    exchangeWithoutCorrections,
    exchangeWithoutCorrectionExpandedTransactionId,
  ]);

  let content = (
    <div className={cn.exchangeLoader}>
      <IconLoader />
    </div>
  );

  let tableContent;

  if (activePermissions && transactionsTypes) {
    if (isLoading || (exchangeListFetching && !exchangeWithoutCorrections)) {
      tableContent = (
        <div className={cn.exchangeLoader}>
          <IconLoader />
        </div>
      );
    } else {
      tableContent = (
        <Table
          title="Exchange without corrections"
          columns={columns}
          data={formattedData || []}
          onSort={() => {}}
          filters={{}}
          sort={{}}
          columnsLabels={columnsLabels}
          onFilterChange={() => {}}
          isLoading={isLoading || exchangeListFetching}
          tableClassName={
            !exchangeWithoutCorrections?.length ? cn.emptyTable : ''
          }
          tableBodyClassName={cn.exchangeTableBody}
          expandableColumns={expandableColumns}
          storeKey="exchange-without-transactions-transaction"
          onExpand={getGeneralLedgerAction}
          isLoadingExpandableRow={glIsLoading || isFetching}
          isSuccessExpandableRow={isSuccess}
          collapseAllRows={collapseAllExchangeWithoutCorrectionRows}
          isFetching={exchangeListFetching}
          transactionTable
          withVerticalScroll
          expandable
          withScroll
          clickable
        />
      );
    }

    content = (
      <div className={cn.exchange}>
        <div className={cn.exchangeWrapper}>{tableContent}</div>
        <Outlet context={{ sendMessage }} />
      </div>
    );
  }

  return content;
};

export default ExchangeWithoutCorrections;
